// columnConfigSidepanel.tsx
import './columnConfigSidepanel.css';
import React, { useEffect, useState } from 'react';
import { Plus, Minus, GripVertical } from 'lucide-react';
import type { TableColumnConfig } from '../../tableMain';
import type { TablePresetsItem } from '../../../../contexts/tableDefinitions';
import { useApiData } from '../../../../contexts/apiService';
import { doc, updateDoc, deleteField, FieldPath } from 'firebase/firestore';
import { auth, db } from '../../../../firebase/firebase';
import ButtonText from '../../../common/button_text/buttonText';
import Checkbox from '../../../common/checkbox/checkbox';
import Dropdown from '../../../common/dropdown/dropdown';
import TextInput from '../../../common/textInput/textInput';


interface ColumnConfigSidePanel {
    isOpen: boolean;
    panelData?: {
        rowData: any;
        colName: string;
        tableName: string;
    };
    columnConfigs: TableColumnConfig[];
    tableName: string;
    onPresetChange?: (preset: TablePresetsItem | null) => void;
}

const ColumnConfigSidePanel: React.FC<ColumnConfigSidePanel> = ({
    isOpen,
    panelData,
    columnConfigs,
    tableName,
    onPresetChange,
}) => {
    const { apiData, handleAddRow, userDetails, setUserDetails, handleDeleteRow } = useApiData();
    const savedPresetId = String(userDetails?.table_presets?.[tableName] ?? '');
    
    const [presetName, setPresetName] = useState('');
    const [selectedPresetId, setSelectedPresetId] = useState(savedPresetId);
    const [activePresetId, setActivePresetId] = useState(savedPresetId);
    const [visibilityError, setVisibilityError] = useState('');

    // sets initial state
    useEffect(() => {
        if (!userDetails?.id) return;

        setActivePresetId(savedPresetId);
        setSelectedPresetId(savedPresetId);
    }, [savedPresetId, tableName, userDetails?.id]);

    // preset creation states
    const [isCreatingPreset, setIsCreatingPreset] = useState(false);
    const [draftColumns, setDraftColumns] = useState<TableColumnConfig[]>([]);
    const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());
    const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
    const [draggedOverColumn, setDraggedOverColumn] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [isPublicPreset, setIsPublicPreset] = useState(false);

    // preset deletion states
    const [isDeletingPreset, setIsDeletingPreset] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    // resolves available presets for the current table and user
    const availablePresets = (apiData?.table_presets ?? []).filter(preset =>
        preset.table_name === tableName &&
        (Number(preset.is_public) === 1 ||
            (!!userDetails?.id && preset.userId === userDetails.id))
    );
    const selectedPreset = availablePresets.find(
        preset => String(preset.id) === selectedPresetId
    );
    const activePreset = availablePresets.find(
        preset => String(preset.id) === activePresetId
    ) ?? null;

    const isSelectedPresetActive =
        !!selectedPreset && selectedPresetId === activePresetId;

    // sends preset changes to parent component (tableMain.tsx)
    useEffect(() => {
        onPresetChange?.(activePreset);
    }, [activePreset, onPresetChange]);

    const handleSelectPreset = (presetId: string) => {
        setSelectedPresetId(presetId);
        setActivationError('');
        setVisibilityError('');
    };

    const handleCreatePreset = () => {
        if (isSaving) return;

        if (isCreatingPreset) {
            setIsCreatingPreset(false);
            return;
        }
        setDraftColumns([...columnConfigs]);
        setSelectedColumns(new Set(columnConfigs.map(column => column.colName)));
        setDraggedColumn(null);
        setDraggedOverColumn(null);
        setPresetName('');
        setIsPublicPreset(false);
        setSaveError('');
        setIsCreatingPreset(true);
    };

    const handleToggleColumn = (colName: string, checked: boolean) => {
        const nextSelected = new Set(selectedColumns);
        if (checked) nextSelected.add(colName);
        else nextSelected.delete(colName);

        setSelectedColumns(nextSelected);

        setDraftColumns(previous => {
            const toggledColumn = previous.find(column => column.colName === colName);
            if (!toggledColumn) return previous;

            const remaining = previous.filter(column => column.colName !== colName);

            if (!checked) {
                return [...remaining, toggledColumn];
            }

            const lastCheckedIndex = remaining.reduce(
                (lastIndex, column, index) =>
                    nextSelected.has(column.colName) ? index : lastIndex,
                -1,
            );

            remaining.splice(lastCheckedIndex + 1, 0, toggledColumn);
            return remaining;
        });
    };

    const handleDrop = (event: React.DragEvent, targetName: string) => {
        event.preventDefault();
        setDraggedOverColumn(null);
        if (!draggedColumn || draggedColumn === targetName) return;
        setDraftColumns(previous => {
            const sourceIndex = previous.findIndex(col => col.colName === draggedColumn);
            const targetIndex = previous.findIndex(col => col.colName === targetName);
            if (sourceIndex < 0 || targetIndex < 0) return previous;
            const next = [...previous];
            const [movedColumn] = next.splice(sourceIndex, 1);
            next.splice(targetIndex, 0, movedColumn);
            return next;
        });
        setDraggedColumn(null);
    };

    const handleSavePreset = async () => {
        if (isSaving) return;

        if (!userDetails?.id) {
            setSaveError('A signed-in user is required.');
            return;
        } else if (!presetName.trim()) {
            setSaveError('A preset name is required.');
            return;
        }

        const columnOrder = draftColumns
            .filter(column => selectedColumns.has(column.colName))
            .map(column => column.colName);

        setIsSaving(true);
        setSaveError('');

        try {
            const success = await handleAddRow('table_presets', {
                userId: userDetails.id,
                table_name: tableName,
                column_config: JSON.stringify(columnOrder),
                preset_name: presetName.trim(),
                is_public: isPublicPreset ? 1 : 0,
            });
            
            if (success) {
                setIsCreatingPreset(false);
            } else {
                setSaveError('Could not save the preset. Please try again.');
            }
        } catch {
            setSaveError('Could not save the preset. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const selectedAuthorName = selectedPreset
        ? selectedPreset.userId === userDetails?.id
            ? userDetails.name
            : selectedPreset.created_by_name || 'Unknown author'
        : '';

    const previewColumns = (() => {
        if (!selectedPreset) return [];

        try {
            const config = typeof selectedPreset.column_config === 'string'
                ? JSON.parse(selectedPreset.column_config)
                : selectedPreset.column_config;

            if (!Array.isArray(config)) return [];

            return config.flatMap(colName => {
                const column = columnConfigs.find(column => column.colName === colName);
                return column ? [column] : [];
            });
        } catch {
            return [];
        }
    })();

    // saving activated table preset
    const [isUpdatingPreset, setIsUpdatingPreset] = useState(false);
    const [activationError, setActivationError] = useState('');

    const handleActivatePreset = async (enabled: boolean) => {
        if (isUpdatingPreset) return;

        const currentUser = auth.currentUser;
        if (!currentUser || (enabled && !selectedPreset)) {
            setActivationError('A signed-in user and selected preset are required.');
            return;
        }

        setIsUpdatingPreset(true);
        setActivationError('');

        try {
            await updateDoc(
                doc(db, 'users', currentUser.uid),
                new FieldPath('table_presets', tableName),
                enabled ? selectedPreset!.id : deleteField(),
            );
            const nextPresets = { ...userDetails?.table_presets };

            if (enabled) {
                nextPresets[tableName] = selectedPreset!.id;
            } else {
                delete nextPresets[tableName];
            }

            setActivePresetId(enabled ? String(selectedPreset!.id) : '');

            if (userDetails) {
                setUserDetails({
                    ...userDetails,
                    table_presets: nextPresets,
                });
            }
        } catch (error) {
            console.error('Could not update active preset:', error);
            setActivationError('Could not update the preset. Please try again.');
        } finally {
            setIsUpdatingPreset(false);
        }
    };

    const handleDeletePreset = async () => {
        if (!selectedPreset || selectedPreset.userId !== userDetails?.id ||
            isDeletingPreset || isSaving || isUpdatingPreset) return;

        const preset = selectedPreset;
        if (!window.confirm(`Delete preset "${preset.preset_name}"?`)) return;

        setIsDeletingPreset(true);
        setDeleteError('');
        let deleted = false;

        try {
            deleted = await handleDeleteRow(new Set([preset.id]), 'table_presets');
            if (!deleted) throw new Error('Delete failed');

            setSelectedPresetId('');
            
            // if the deleted preset was the currently active preset, clear the firebase doc
            if (String(userDetails.table_presets?.[tableName]) === String(preset.id)) {
                const currentUser = auth.currentUser;
                if (!currentUser) throw new Error('Not authenticated');

                await updateDoc(
                    doc(db, 'users', currentUser.uid),
                    new FieldPath('table_presets', tableName),
                    deleteField(),
                );

                setActivePresetId('');
                setUserDetails(previous => {
                    if (!previous || previous.id !== preset.userId) return previous;
                    const nextPresets = { ...previous.table_presets };
                    delete nextPresets[tableName];
                    return { ...previous, table_presets: nextPresets };
                });
            }
        } catch (error) {
            console.error('Could not complete preset deletion:', error);
            setDeleteError(deleted
                ? 'Preset deleted, but its saved activation could not be cleared.'
                : 'Could not delete the preset. Please try again.');
        } finally {
            setIsDeletingPreset(false);
        }
    };

    if (!isOpen) return null;
    
    return (
        <div className="columnConfigSidePanel-mainContainer">
            <div className="columnConfigSidePanel-titleContainer">
                Custom column configuration
            </div>
            <div className="columnConfigSidePanel-topContainer">
                <div
                    className="columnConfigSidePanel-createPreset"
                    onClick={handleCreatePreset}
                >   
                    <div
                        className={`columnConfigSidePanel-createIcon ${
                            isCreatingPreset ? 'columnConfigSidePanel-createIcon--open' : ''
                        }`}
                        aria-hidden="true"
                    >
                        {isCreatingPreset ? <Minus size={18} /> : <Plus size={18} />}
                    </div> 
                    {isCreatingPreset ? 'Cancel Creation' : 'Create new preset'}
                </div>
            </div>
            {isCreatingPreset && (
                <>
                    <div className="columnConfigSidePanel-presetNameInput">
                        <TextInput
                            value={presetName}
                            onChange={setPresetName}
                            placeholder="Enter table preset name"
                            width={300}
                            disabled={isSaving}
                        />
                    </div>
                    <div className="columnConfigSidePanel-columnList">
                        {draftColumns.map((column, index) => (
                            <div
                                key={column.colName}
                                className={`sequenceComponent-item ${
                                    draggedColumn === column.colName ? 'sequenceComponent-item--dragging' : ''
                                } ${
                                    draggedOverColumn === column.colName ? 'sequenceComponent-item--dragover' : ''
                                }`}
                                draggable
                                onDragStart={event => {
                                    setDraggedColumn(column.colName);
                                    event.dataTransfer.effectAllowed = 'move';
                                    event.dataTransfer.setData('text/plain', column.colName);
                                }}
                                onDragOver={event => {
                                    if (!draggedColumn) return;
                                    event.preventDefault();
                                    event.dataTransfer.dropEffect = 'move';
                                    setDraggedOverColumn(draggedColumn !== column.colName ? column.colName : null);
                                }}
                                onDragLeave={() => setDraggedOverColumn(null)}
                                onDrop={event => handleDrop(event, column.colName)}
                                onDragEnd={() => {
                                    setDraggedColumn(null);
                                    setDraggedOverColumn(null);
                                }}
                            >
                                <div className="sequenceItem-mainContainer">
                                    <div className="sequenceItem-topRow">
                                        <div className="sequenceItem-gripButton">
                                            <GripVertical size={16} style={{ color: 'var(--color-grey-light-text)' }} />
                                        </div>
                                        <div className="columnConfigList-labelContainer" style={{ pointerEvents: 'auto' }}>
                                            <span className="columnConfigList-numberContainer">{index + 1}.</span>
                                            <Checkbox
                                                label={column.colHeader ?? column.colName}
                                                checked={selectedColumns.has(column.colName)}
                                                onChange={checked => handleToggleColumn(column.colName, checked)}
                                                labelGap={4}
                                                ghostUnchecked={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="columnConfigSidePanel-publicButton">
                        <Checkbox
                            label="Public preset"
                            checked={isPublicPreset}
                            onChange={setIsPublicPreset}
                            isDisabled={isSaving}
                            labelGap={4}
                        />
                    </div>
                    {saveError && <div role="alert">{saveError}</div>}
                    <div className="columnConfigSidePanel-actionButtons">
                        <ButtonText
                            buttonText={"Save"}
                            buttonColor={'blue'}
                            buttonStyle={"modern"}
                            disabled={isSaving}
                            onClick={handleSavePreset}
                        />
                        <ButtonText
                            buttonText={"Cancel"}
                            buttonColor={'red'}
                            buttonStyle={"modern"}
                            onClick={() => setIsCreatingPreset(false)}
                            disabled={isSaving}
                        />
                    </div>
                    <div className="columnConfigSidePanel-dividerLine"/>
                </>
            )}
            <div className="columnConfigSidePanel-middleContainer">
                <Dropdown
                    placeholder="Select table preset"
                    className="dropdown-inputMatch"
                    options={availablePresets.map(preset => ({
                        value: String(preset.id),
                        label: preset.preset_name,
                    }))}
                    value={selectedPreset ? selectedPresetId : ''}
                    onChange={handleSelectPreset}
                    isDisabled={isSaving || isUpdatingPreset}
                />
            </div>
            {selectedPreset && (
                <div className="columnConfigSidePanel-presetPreview">
                    <div className="columnConfigSidePanel-previewTitle">
                        Created by - {selectedAuthorName}
                    </div>
                    <div className="columnConfigSidePanel-columnList">
                        {previewColumns.map((column, index) => (
                            <div key={column.colName} className="sequenceItem-mainContainer">
                                <div className="sequenceItem-topRow">
                                    <div className="columnConfigList-labelContainer">
                                        <span className="columnConfigList-numberContainer">
                                            {index + 1}.
                                        </span>
                                        <span>{column.colHeader ?? column.colName}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div>
                        {selectedPreset.userId === userDetails?.id && (
                            <div className="columnConfigSidePanel-publicButton">
                                <Checkbox
                                    key={`${selectedPreset.id}-${selectedPreset.is_public}`}
                                    label="Public preset"
                                    defaultChecked={selectedPreset.is_public === 1}
                                    isDisabled={isSaving || isUpdatingPreset}
                                    labelGap={4}
                                    editConfig={{
                                        tableName: 'table_presets',
                                        rowId: selectedPreset.id,
                                        columnName: 'is_public',
                                        onSuccess: () => setVisibilityError(''),
                                        onError: () => setVisibilityError(
                                            'Could not update preset visibility. Please try again.'
                                        ),
                                    }}
                                />
                                <ButtonText
                                    buttonText="Delete"
                                    buttonColor="red"
                                    buttonStyle="modern"
                                    onClick={handleDeletePreset}
                                    disabled={isSaving || isUpdatingPreset || isDeletingPreset}
                                />
                            </div>
                        )}
                        {deleteError && <div role="alert">{deleteError}</div>}
                        {visibilityError && <div role="alert">{visibilityError}</div>}
                    </div>
                    <div className="columnConfigSidePanel-activateButton">
                        <Checkbox
                            label="Activate table preset"
                            checked={isSelectedPresetActive}
                            onChange={handleActivatePreset}
                            isDisabled={!selectedPreset || isSaving || isUpdatingPreset}
                            labelGap={4}
                        />
                        {activationError && <div role="alert">{activationError}</div>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColumnConfigSidePanel;